/**
 * クライアントサイド遷移が固まったことを検知して脱出するウォッチドッグ（PRODUCT_DEV-612）。
 *
 * 仕組みと「なぜ誤検知しないか」は `@/lib/navigationWatchdog` の冒頭コメントを参照。
 * 何も描画しない。`RootLayout` に1つだけ置く。
 */
import { useEffect } from "react";
import { useLocation } from "react-router";
import { breadcrumb } from "@/lib/navDiagnostics";
import {
  evaluateNavigation,
  NAV_POLL_INTERVAL_MS,
  readAndClearStuckReport,
  recoverFromStuckNavigation,
} from "@/lib/navigationWatchdog";

export const NavigationWatchdog: React.FC = () => {
  const location = useLocation();
  // useLocation が返すのは「React がコミット済み」の location。transition が
  // コミットされない限りここは更新されないので、ブラウザの URL との比較で検知できる。
  const committed = location.pathname + location.search;

  // 直前のフルページロードで固まっていた場合、そのレポートをここで再掲する。
  // 発火時の出力は復帰のためのリロードで流れてしまうため、これが無いと
  // 「Preserve log」を事前に ON にしていた人しか報告を回収できない。
  useEffect(() => {
    const json = readAndClearStuckReport();
    if (!json) return;
    console.error(
      "[nav] stuck-navigation report from the previous page load",
      json,
    );
  }, []);

  useEffect(() => {
    // effect が走る＝transition がコミットされた、の意。ここが出ないまま URL だけ
    // 進んでいる状態がフリーズ。
    breadcrumb("nav:commit", { path: committed });

    let stuckSince: number | null = null;
    const intervalId = window.setInterval(() => {
      const current = window.location.pathname + window.location.search;
      const verdict = evaluateNavigation({
        committed,
        current,
        stuckSince,
        now: Date.now(),
      });
      stuckSince = verdict.stuckSince;
      if (!verdict.shouldRecover) return;

      window.clearInterval(intervalId);
      void recoverFromStuckNavigation({
        from: committed,
        to: current,
        stuckMs: Date.now() - (verdict.stuckSince ?? Date.now()),
      });
    }, NAV_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [committed]);

  return null;
};
